import React from "react";
import { ArrowRight, CircleCheck as CheckCircle2, Clock, Sparkles } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
interface FallbackArticle {
  id: string;
  title: string;
  excerpt: string;
  category: string;
  readTime: string;
  clinicalAnswer: string;
  image: string;
  imageSrcset: string;
  imageSizes: string;
  alt: string;
  link: string;
}
const fallbackArticles: FallbackArticle[] = [{
  id: "strawberries-dogs",
  title: "Can dogs safely eat fresh strawberries?",
  excerpt: "Packed with antioxidants, high in fiber, and rich in vitamin C, strawberries provide natural benefits when offered in measured moderation.",
  category: "Canine Nutrition",
  readTime: "4 min read",
  clinicalAnswer: "Yes. Strawberries are non-toxic and contain the enzyme malic acid, which naturally helps whiten canine teeth. Always serve fresh, washed, and sliced.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/strawberries_dog_nutrition.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/strawberries_dog_nutrition.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/strawberries_dog_nutrition.png-wvc-sizes",
  alt: "Fresh organic strawberries prepared safely for pets",
  link: "/NutritionPage"
}, {
  id: "grain-free-cats",
  title: "The clinical facts behind grain-free feline diets",
  excerpt: "Understanding feline obligate carnivore biology, dietary carbohydrate thresholds, and recent veterinary cardiology evaluations.",
  category: "Feline Dietetics",
  readTime: "6 min read",
  clinicalAnswer: "Cats require high animal protein and specific amino acids like taurine. Grain-free is not mandatory unless an adverse food reaction is clinically diagnosed.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/grain_free_cat_food.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/grain_free_cat_food.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/grain_free_cat_food.png-wvc-sizes",
  alt: "High-protein natural feline meal bowl with wholesome ingredients",
  link: "/NutritionPage"
}, {
  id: "omega-fatty-acids",
  title: "Essential omega-3 and omega-6 fatty acids for pets",
  excerpt: "How EPA and DHA fatty acids regulate cellular inflammation, maintain renal integrity, and optimize epidermal lipid barriers.",
  category: "Clinical Supplements",
  readTime: "5 min read",
  clinicalAnswer: "Marine-derived EPA and DHA reduce inflammatory joint markers and pruritus. Dosages should always be calibrated to lean body mass.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/omega_oils_pet_health.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/omega_oils_pet_health.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/omega_oils_pet_health.png-wvc-sizes",
  alt: "Cold-pressed pure marine omega oil supplement for pets",
  link: "/NutritionPage"
}];
function NutritionArticlesConsumer() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
        {[0, 1, 2].map(i => <div key={i} data-index={i} className="flex flex-col overflow-hidden rounded-xl border border-border bg-card p-6 shadow-xs animate-pulse">
            <div className="h-52 w-full rounded-lg bg-muted" />
            <div className="mt-5 h-4 w-28 rounded bg-muted" />
            <div className="mt-3 h-6 w-3/4 rounded bg-muted" />
            <div className="mt-3 h-14 w-full rounded bg-muted" />
            <div className="mt-6 h-20 w-full rounded-md bg-muted/60" />
            <div className="mt-6 flex items-center justify-between pt-4 border-t border-border">
              <div className="h-4 w-20 rounded bg-muted" />
              <div className="h-8 w-24 rounded bg-muted" />
            </div>
          </div>)}
      </div>;
  }
  const hasLivePosts = Boolean(posts && posts.length > 0);
  if (error || !hasLivePosts) {
    return <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
        {fallbackArticles.map((article, i) => <article key={article.id} data-index={i} className="group flex flex-col justify-between overflow-hidden rounded-xl border border-border bg-card shadow-xs transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1 hover:border-border/80 hover:shadow-md">
            <div>
              <div className="relative aspect-16/10 w-full overflow-hidden bg-muted">
                <img src={article.image} data-wvc-srcset={article.imageSrcset} data-wvc-sizes={article.imageSizes} alt={article.alt} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-103" />
                <div className="absolute top-3 left-3">
                  <Badge variant="secondary" className="border border-border bg-background/95 px-2.5 py-1 text-xs font-semibold text-foreground backdrop-blur-xs">
                    {article.category}
                  </Badge>
                </div>
              </div>

              <div className="p-6">
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  <span className="inline-flex items-center gap-1 text-accent">
                    <CheckCircle2 className="size-3.5" />
                    Veterinary Reviewed
                  </span>
                  <span>•</span>
                  <span className="inline-flex items-center gap-1">
                    <Clock className="size-3.5" />
                    {article.readTime}
                  </span>
                </div>

                <h3 className="mt-3 font-serif text-xl font-semibold leading-snug text-card-foreground group-hover:text-foreground">
                  {article.title}
                </h3>

                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  {article.excerpt}
                </p>

                <div className="mt-5 rounded-lg border-l-4 border-accent border-y border-r border-border bg-background/60 p-3.5">
                  <p className="text-xs font-bold uppercase tracking-wider text-accent">
                    Clinical Takeaway
                  </p>
                  <p className="mt-1 text-xs leading-relaxed text-foreground/90">
                    {article.clinicalAnswer}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between border-t border-border bg-muted/20 px-6 py-4">
              <span className="text-xs font-medium text-muted-foreground">
                Updated this week
              </span>
              <Button variant="ghost" size="sm" className="gap-1 px-2 font-semibold text-accent hover:text-accent-foreground" asChild>
                <Link to={article.link}>
                  Read article
                  <ArrowRight className="size-3.5 transition-transform duration-200 group-hover:translate-x-0.5" />
                </Link>
              </Button>
            </div>
          </article>)}
      </div>;
  }
  return <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
      {posts.map((post, i) => {
      const categoryName = post.categories?.[0]?.name || "Nutrition Guide";
      const cleanExcerpt = post.excerpt ? post.excerpt.replace(/<[^>]+>/g, "").slice(0, 130) + "..." : "Essential nutrition metrics, dietary guidelines, and clinical feeding recommendations for pet wellness.";
      return <article key={post.id} data-index={i} className="group flex flex-col justify-between overflow-hidden rounded-xl border border-border bg-card shadow-xs transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1 hover:border-border/80 hover:shadow-md">
            <div>
              <div className="relative aspect-16/10 w-full overflow-hidden bg-muted">
                {post.featuredImage ? <img src={post.featuredImage} alt={post.title} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-103" /> : <div className="flex h-full w-full items-center justify-center bg-muted text-muted-foreground">
                    <Sparkles className="size-8 stroke-[1.5]" />
                  </div>}
                <div className="absolute top-3 left-3">
                  <Badge variant="secondary" className="border border-border bg-background/95 px-2.5 py-1 text-xs font-semibold text-foreground backdrop-blur-xs">
                    {categoryName}
                  </Badge>
                </div>
              </div>

              <div className="p-6">
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  <span className="inline-flex items-center gap-1 text-accent">
                    <CheckCircle2 className="size-3.5" />
                    Veterinary Reviewed
                  </span>
                  <span>•</span>
                  <span className="inline-flex items-center gap-1">
                    <Clock className="size-3.5" />
                    5 min read
                  </span>
                </div>

                <h3 className="mt-3 font-serif text-xl font-semibold leading-snug text-card-foreground group-hover:text-foreground">
                  {post.title}
                </h3>

                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  {cleanExcerpt}
                </p>

                <div className="mt-5 rounded-lg border-l-4 border-accent border-y border-r border-border bg-background/60 p-3.5">
                  <p className="text-xs font-bold uppercase tracking-wider text-accent">
                    Clinical Note
                  </p>
                  <p className="mt-1 text-xs leading-relaxed text-foreground/90">
                    Always consult your veterinarian before adjusting baseline caloric density or introducing concentrated supplements.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between border-t border-border bg-muted/20 px-6 py-4">
              <span className="text-xs font-medium text-muted-foreground">
                {post.date ? new Date(post.date).toLocaleDateString() : "Editorial Guide"}
              </span>
              <Button variant="ghost" size="sm" className="gap-1 px-2 font-semibold text-accent hover:text-accent-foreground" asChild>
                <Link to={post.link || "/NutritionPage"}>
                  Read article
                  <ArrowRight className="size-3.5 transition-transform duration-200 group-hover:translate-x-0.5" />
                </Link>
              </Button>
            </div>
          </article>;
    })}
    </div>;
}
export default function Articles() {
  const wpQueryParams = {
    post_type: "post",
    per_page: 3,
    order: "desc",
    orderby: "date"
  };
  const wpQuery = new WP_Query(wpQueryParams);
  return <section id="featured-nutrition-articles" data-nav="light" className="bg-background py-16 md:py-24">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-12 flex flex-col items-start justify-between gap-6 border-b border-border pb-8 md:flex-row md:items-end">
          <div className="max-w-2xl">
            <span className="text-xs font-bold uppercase tracking-widest text-accent">
              Clinical Food & Dietary Science
            </span>
            <h2 className="mt-2 font-serif text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
              Featured nutrition articles
            </h2>
            <p className="mt-3 text-base leading-relaxed text-muted-foreground">
              Evidence-based dietary evaluations, verified safe food lists, and clinical feeding protocols reviewed by veterinary practitioners.
            </p>
          </div>

          <div className="shrink-0">
            <Button variant="outline" size="default" className="border-border bg-background font-semibold text-foreground hover:bg-muted" asChild>
              <Link to="/NutritionPage">
                View all nutrition guides
                <ArrowRight className="size-4" />
              </Link>
            </Button>
          </div>
        </div>

        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 3,
        order: "desc",
        orderby: "date",
        status: ["publish"]
      }}>
          <NutritionArticlesConsumer />
        </PostsProvider>
      </div>
    </section>;
}